import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useInView } from "framer-motion";
import { Reveal, SectionHead, Wrap } from "./Motion";

const chapters = [
  { n: "01", kicker: "Heritage", title: "Seven generations. One family.", body: "The Brodericks have bred and sold horses from Ballypatrick, Tipperary for over a century.", stat: ["7", "Generations"], img: "https://ballypatrickauctions.com/wp-content/uploads/2025/10/dp.png" },
  { n: "02", kicker: "Selection", title: "Selected by Greg Broderick.", body: "Olympian, Nations Cup rider and producer of five-star horses. Every lot is ridden, assessed and approved by Greg personally before it earns its place in the collection.", stat: ["100%", "Personally approved"], img: "https://ballypatrickauctions.com/wp-content/uploads/2024/11/b82e41bd-6fcc-4477-9c51-94f7974bc0c0-980x653-1.jpg" },
  { n: "03", kicker: "Honesty", title: "Matched to your level.", body: "Amateur, young rider, Grand Prix or pony team — we tell you who each horse is for.", stat: ["5", "Rider levels"], img: "https://ballypatrickauctions.com/wp-content/uploads/2025/12/at_1.jpg" },
  { n: "04", kicker: "Ground", title: "Raised on the Golden Vale.", body: "500 acres of limestone-rich pasture in Tipperary's Golden Vale — the same ground that has raised Ireland's finest horses for generations.", stat: ["500", "Acres"], img: "https://ballypatrickauctions.com/wp-content/uploads/2024/11/40fe4cb0-fa01-4772-9aec-c0002d51e9c4-980x653-1.jpg" },
];

const ChapterText = ({ c, i, onActive }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { margin: "-45% 0px -45% 0px" });
  useEffect(() => { if (inView) onActive(i); }, [inView, i, onActive]);
  return (
    <div ref={ref} className="min-h-[70vh] flex flex-col justify-center" data-testid={`ballypatrick-manifesto-chapter-${i + 1}`}>
      <motion.div initial={{ opacity: 0.25 }} animate={{ opacity: inView ? 1 : 0.25 }} transition={{ duration: 0.6 }}>
        <div className="flex items-center gap-4">
          <span className="font-display text-6xl num-outline">{c.n}</span>
          <span className="eyebrow">{c.kicker}</span>
        </div>
        <h3 className="mt-6 font-display text-balance text-4xl xl:text-6xl font-medium leading-[1.02] text-[#F8F6F0]">{c.title}</h3>
        <p className="mt-5 text-base text-[#AEB7C9] leading-relaxed max-w-md">{c.body}</p>
        <div className="mt-8 flex items-baseline gap-3">
          <span className="font-display text-5xl text-[#D4AF37]">{c.stat[0]}</span>
          <span className="text-xs tracking-[0.18em] uppercase text-[#6F7C97]">{c.stat[1]}</span>
        </div>
      </motion.div>
    </div>
  );
};

const MobileCard = ({ c, i }) => (
  <article className="relative w-[82vw] max-w-[360px] aspect-[3/4] overflow-hidden border border-white/10" data-testid={`ballypatrick-manifesto-card-${i + 1}`}>
    <img src={c.img} alt={c.title} className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
    <div className="absolute inset-0 bg-gradient-to-t from-[#0B1630] via-[#0B1630]/55 to-[#0B1630]/10" />
    <div className="absolute top-4 left-4 right-4 flex items-center justify-between">
      <span className="eyebrow">{c.kicker}</span>
      <span className="font-mono text-[11px] text-[#F8F6F0]/70">{c.n} / 04</span>
    </div>
    <div className="absolute bottom-0 inset-x-0 p-5">
      <div className="flex items-baseline gap-2 mb-2">
        <span className="font-display text-4xl text-[#D4AF37]">{c.stat[0]}</span>
        <span className="text-[10px] tracking-[0.18em] uppercase text-[#AEB7C9]">{c.stat[1]}</span>
      </div>
      <h3 className="font-display text-2xl text-[#F8F6F0] leading-tight">{c.title}</h3>
      <p className="mt-2 text-sm text-[#AEB7C9] leading-relaxed">{c.body}</p>
    </div>
  </article>
);

export const Manifesto = () => {
  const [active, setActive] = useState(0);
  const [dot, setDot] = useState(0);
  const rowRef = useRef(null);
  const onScroll = () => { const el = rowRef.current; if (el) setDot(Math.round(el.scrollLeft / (el.firstChild?.offsetWidth + 16 || 1))); };

  return (
    <section id="manifesto" className="relative py-20 lg:py-32 navy-glow" data-testid="manifesto-section">
      <Wrap>
        <SectionHead eyebrow="Why Ballypatrick" id="manifesto" title={<>Anyone can run an auction. <em className="text-[#D4AF37]">Few can stand behind every horse.</em></>} />

        <div className="mt-10 lg:hidden">
          <div ref={rowRef} onScroll={onScroll} className="snap-row -mx-5 px-5" data-testid="manifesto-snap-row">
            {chapters.map((c, i) => <MobileCard key={c.n} c={c} i={i} />)}
          </div>
          <div className="mt-4 flex gap-2" data-testid="manifesto-dots">
            {chapters.map((_, i) => <span key={i} className={`h-1 transition-all duration-500 ${dot === i ? "w-8 bg-[#D4AF37]" : "w-3 bg-white/20"}`} />)}
          </div>
        </div>

        <div className="hidden lg:grid grid-cols-12 gap-16 mt-16">
          <div className="col-span-6 sticky top-28 self-start h-[calc(100vh-9rem)]">
            <div className="relative h-full w-full overflow-hidden clip-frame border border-white/5">
              <AnimatePresence mode="sync">
                <motion.img key={active} src={chapters[active].img} alt={chapters[active].title}
                  initial={{ opacity: 0, scale: 1.06 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                  className="absolute inset-0 h-full w-full object-cover" data-testid="manifesto-sticky-image" />
              </AnimatePresence>
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B1630]/70 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between">
                <span className="font-display text-8xl num-outline leading-none">{chapters[active].n}</span>
                <div className="flex gap-2">{chapters.map((_, i) => <span key={i} className={`h-1 transition-all duration-500 ${active === i ? "w-10 bg-[#D4AF37]" : "w-4 bg-white/25"}`} />)}</div>
              </div>
            </div>
          </div>
          <div className="col-span-6">
            {chapters.map((c, i) => <ChapterText key={c.n} c={c} i={i} onActive={setActive} />)}
          </div>
        </div>
      </Wrap>
    </section>
  );
};
